// src/page/NotificationList.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';

export default function NotificationList() {
    useAuthGuard();
    const navigate = useNavigate();

    // ===== URL state =====
    const [sp, setSp] = useSearchParams();
    const initPage = Number(sp.get('page') || 1);
    const initSize = Number(sp.get('size') || 20);
    const initQ = sp.get('q') || '';
    const initTab = sp.has('tab') ? Number(sp.get('tab')) : -1; // -1 = all
    const initSList = sp.get('s') ? sp.get('s').split(',').map(Number).filter(n => [0, 1].includes(n)) : [];
    const initSortKey = sp.get('sort') || 'created'; // title|delivery|created
    const initSortDir = sp.get('dir') || 'desc';      // asc|desc

    // ===== UI / Query state =====
    const [keywordInput, setKeywordInput] = useState(initQ);
    const [searchKeyword, setSearchKeyword] = useState(initQ);
    const [statusChecks, setStatusChecks] = useState({
        active: initSList.includes(0),    // 配信済み
        withdrawn: initSList.includes(1), // 未配信
    });

    const [s, setS] = useState(initTab);
    const [querySList, setQuerySList] = useState(initSList);

    const [sort, setSort] = useState({ key: initSortKey, dir: initSortDir });
    const toggleSort = (key) =>
        setSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

    const [page, setPage] = useState(initPage);
    const [pageSize, setPageSize] = useState(initSize);

    // ===== Data =====
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [counts, setCounts] = useState({ all: 0, active: 0, withdrawn: 0 });
    const [headerCounts, setHeaderCounts] = useState({ all: 0, active: 0, withdrawn: 0 });

    // ===== Derived (pagination UI) =====
    const pageCount = Math.max(1, Math.ceil((total || 0) / pageSize));
    const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const last = Math.min(total, page * pageSize);
    const pages = useMemo(() => Array.from({ length: pageCount }, (_, i) => i + 1), [pageCount]);

    // ===== Sync state -> URL =====
    useEffect(() => {
        const p = new URLSearchParams();
        if (page !== 1) p.set('page', String(page));
        if (pageSize !== 20) p.set('size', String(pageSize));
        if (searchKeyword) p.set('q', searchKeyword);

        if (querySList.length) p.set('s', querySList.join(','));
        else if (s >= 0) p.set('tab', String(s));

        if (sort.key) p.set('sort', sort.key);
        if (sort.dir) p.set('dir', sort.dir);

        setSp(p, { replace: true });
    }, [page, pageSize, searchKeyword, querySList, s, sort, setSp]);

    // ===== Fetch list =====
    useEffect(() => {
        const ac = new AbortController();
        (async () => {
            try {
                setLoading(true);
                setError('');
                const token = localStorage.getItem('access_token');

                const url = new URL(`${API_BASE_URL}/v1/notification`);
                url.searchParams.set('page', String(page));
                url.searchParams.set('size', String(pageSize));
                url.searchParams.set('sort', sort.key);
                url.searchParams.set('dir', sort.dir);

                if (querySList.length > 0) url.searchParams.set('s', querySList.join(','));
                else if (s >= 0) url.searchParams.set('s', String(s));

                if (searchKeyword.trim() !== '') url.searchParams.set('q', searchKeyword.trim());

                const res = await fetch(url.toString(), {
                    headers: { Authorization: token ? `Bearer ${token}` : '', Accept: 'application/json' },
                    signal: ac.signal,
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();

                setItems(Array.isArray(data.items) ? data.items : []);
                setTotal(Number(data.total) || 0);

                if (data.counts) {
                    // API returns: {all, delivered, pending}
                    const mapped = {
                        all: Number(data.counts.all) || 0,
                        active: Number(data.counts.delivered) || 0,
                        withdrawn: Number(data.counts.pending) || 0,
                    };
                    setCounts(mapped);
                    const noFilter = (searchKeyword.trim() === '') && (querySList.length === 0) && (s === -1);
                    if (noFilter) setHeaderCounts(mapped);
                }

                if (data.size && Number(data.size) !== pageSize) setPageSize(Number(data.size));
            } catch (e) {
                if (e.name !== 'AbortError') setError('プッシュ通知の取得に失敗しました');
            } finally {
                setLoading(false);
            }
        })();
        return () => ac.abort();
    }, [API_BASE_URL, page, pageSize, s, searchKeyword, querySList, sort]);

    // ===== Keep page in range =====
    useEffect(() => {
        if (page > pageCount) setPage(pageCount);
        if (page < 1) setPage(1);
    }, [page, pageCount]);

    // ===== Handlers =====
    const activateTab = (nextS) => {
        setS(nextS);
        setPage(1);
        setQuerySList([]);
        setStatusChecks({ active: nextS === 0, withdrawn: nextS === 1 });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setSearchKeyword(keywordInput);
        const selected = [];
        if (statusChecks.active) selected.push(0);    // delivered
        if (statusChecks.withdrawn) selected.push(1); // pending
        setQuerySList(selected);
        setS(-1);
        setPage(1);
    };

    const handleClear = () => {
        setKeywordInput('');
        setSearchKeyword('');
        setStatusChecks({ active: false, withdrawn: false });
        setQuerySList([]);
        setS(-1);
        setPage(1);
    };

    const goPrev = () => setPage(p => Math.max(1, p - 1));
    const goNext = () => setPage(p => Math.min(pageCount, p + 1));

    // ===== Helpers =====
    const pad = (n) => String(n).padStart(2, '0');
    const fmt = (s) => {
        if (!s) return '-';
        const d = new Date(s);
        if (isNaN(d)) return s;
        return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    const statusLabel = (it) => (it.deliveryDate ? '配信済み' : '未配信');

    // ---- CSV Export ----
    const handleExportCsv = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const url = new URL(`${API_BASE_URL}/v1/notification/export`);

            // フィルタは一覧と同じ優先度で付与
            if (querySList.length > 0) url.searchParams.set('s', querySList.join(','));
            else if (s >= 0) url.searchParams.set('s', String(s));

            if (searchKeyword.trim() !== '') url.searchParams.set('q', searchKeyword.trim());
            if (sort.key) url.searchParams.set('sort', sort.key);
            if (sort.dir) url.searchParams.set('dir', sort.dir);

            const res = await fetch(url.toString(), {
                headers: {
                    Authorization: token ? `Bearer ${token}` : '',
                    Accept: 'text/csv',
                },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const blob = await res.blob();

            // ファイル名は Content-Disposition 優先、なければ既定名
            let filename = `プッシュ通知一覧-${new Date().toISOString().replace(/\D/g, '').slice(0, 14)}.csv`;
            const cd = res.headers.get('content-disposition');
            if (cd) {
                const mStar = cd.match(/filename\*\s*=\s*[^']*'(?:[^']*)'([^;]+)/i);
                const m = cd.match(/filename\s*=\s*([^;]+)/i);
                const raw = mStar ? mStar[1] : (m ? m[1] : '');
                if (raw) filename = raw.trim().replace(/^"(.*)"$/, '$1');
            }

            const objectUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(objectUrl);
        } catch (e) {
            console.error(e);
            alert('CSVダウンロードに失敗しました');
        }
    };

    // ===== Render =====
    return (
        <Layout>
            <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item"><a href="/notification">プッシュ通知管理</a></li>
                    <li className="breadcrumb-item active">プッシュ通知一覧</li>
                </ol>
            </nav>

            <div className="mb-9">
                <div className="row g-3 mb-4">
                    <div className="col-auto">
                        <h2 className="mb-0">プッシュ通知一覧</h2>
                    </div>
                </div>

                {/* Tabs */}
                <ul className="nav nav-links mb-3 mb-lg-2 mx-n3">
                    <li className="nav-item">
                        <a href="#"
                            aria-current={s === -1 ? 'page' : undefined}
                            className={`nav-link ${s === -1 ? 'active' : ''}`}
                            onClick={(e) => { e.preventDefault(); activateTab(-1); }}>
                            <span>全て </span>
                            <span className="text-body-tertiary fw-semibold">({headerCounts.all})</span>
                        </a>
                    </li>
                    <li className="nav-item">
                        <a href="#"
                            className={`nav-link ${s === 0 ? 'active' : ''}`}
                            onClick={(e) => { e.preventDefault(); activateTab(0); }}>
                            <span>配信済み </span>
                            <span className="text-body-tertiary fw-semibold">({headerCounts.active})</span>
                        </a>
                    </li>
                    <li className="nav-item">
                        <a href="#"
                            className={`nav-link ${s === 1 ? 'active' : ''}`}
                            onClick={(e) => { e.preventDefault(); activateTab(1); }}>
                            <span>未配信 </span>
                            <span className="text-body-tertiary fw-semibold">({headerCounts.withdrawn})</span>
                        </a>
                    </li>
                </ul>

                <div id="notifications">
                    <div className="mb-4">
                        <form className="position-relative w-100" onSubmit={handleSubmit}>
                            <div className="d-flex flex-wrap gap-3 align-items-end">
                                {/* Keyword */}
                                <div className="search-box">
                                    <input
                                        aria-label="Search"
                                        className="form-control search-input search"
                                        placeholder="通知ID/タイトル/内容/URL"
                                        type="search"
                                        name="keyword"
                                        value={keywordInput}
                                        onChange={(e) => setKeywordInput(e.target.value)}
                                    />
                                    <span className="fas fa-search search-box-icon"></span>
                                </div>

                                {/* Status checks */}
                                <div>
                                    <label className="form-label mb-1 d-block">ステータス</label>
                                    <div className="d-flex flex-wrap gap-2">
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox" id="status-active"
                                                checked={statusChecks.active}
                                                onChange={(e) => setStatusChecks(v => ({ ...v, active: e.target.checked }))} />
                                            <label className="form-check-label" htmlFor="status-active">配信済み</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox" id="status-withdrawn"
                                                checked={statusChecks.withdrawn}
                                                onChange={(e) => setStatusChecks(v => ({ ...v, withdrawn: e.target.checked }))} />
                                            <label className="form-check-label" htmlFor="status-withdrawn">未配信</label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex gap-2 mt-3">
                                <button type="submit" className="btn btn-primary">検索</button>
                                <button type="button" className="btn btn-outline-secondary" onClick={handleClear}>クリア</button>

                                <div className="ms-xxl-auto d-flex gap-3">
                                    <button className="btn btn-link text-body me-4 px-0" type="button" onClick={handleExportCsv}>
                                        <span className="fa-solid fa-file-export fs-9 me-2"></span>CSVダウンロード
                                    </button>
                                    <button
                                        className="btn btn-primary"
                                        id="addBtn"
                                        type="button"
                                        onClick={() => navigate('/notification/new')}
                                    >
                                        <span className="fas fa-plus me-2"></span>新規登録
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>

                    <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
                        <div className="table-responsive scrollbar mx-n1 px-1">
                            <table className="table fs-9 mb-0">
                                <thead>
                                    <tr>
                                        <th className="align-middle ps-3">通知ID</th>
                                        <th className="align-middle ps-3" role="button"
                                            onClick={() => { toggleSort('title'); setPage(1); }}>
                                            タイトル <span className="fas ms-1 text-muted fa-sort" />
                                        </th>
                                        <th className="align-middle ps-3">内容</th>
                                        <th className="align-middle ps-3">遷移先URL</th>
                                        <th className="align-middle ps-3" role="button"
                                            onClick={() => { toggleSort('delivery'); setPage(1); }}>
                                            配信日時 <span className="fas ms-1 text-muted fa-sort" />
                                        </th>
                                        <th className="align-middle ps-3">配信状況</th>
                                        <th className="align-middle ps-3">配信対象数</th>
                                        <th className="align-middle ps-3">開封数</th>
                                        <th className="align-middle ps-3"></th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {loading && (
                                        <tr><td colSpan={9} className="text-center py-4">読み込み中...</td></tr>
                                    )}
                                    {!loading && error && (
                                        <tr><td colSpan={9} className="text-danger py-4">{error}</td></tr>
                                    )}
                                    {!loading && !error && items.length === 0 && (
                                        <tr><td colSpan={9} className="text-center py-4">データがありません</td></tr>
                                    )}
                                    {!loading && !error && items.map((it) => (
                                        <tr key={it.notificationId}>
                                            <td>{it.notificationId}</td>
                                            <td>{it.notificationTitle || '-'}</td>
                                            <td className="text-truncate" style={{ maxWidth: '320px' }}>
                                                {it.notificationDescription ?? '-'}
                                            </td>
                                            <td className="text-truncate" style={{ maxWidth: '240px' }}>
                                                {it.notificationUrl ?? '-'}
                                            </td>
                                            <td>{fmt(it.deliveryDate)}</td>
                                            <td>{it.deliveryDate ? '配信済み' : '未配信'}</td>
                                            <td>{Number.isFinite(it.deliveryUserCount) ? it.deliveryUserCount : (it.deliveryUserCount ?? '-')}</td>
                                            <td>{Number.isFinite(it.openCount) ? it.openCount : (it.openCount ?? '-')}</td>
                                            <td>
                                                <button className="btn btn-sm btn-outline-primary"
                                                    onClick={() => navigate(`/notification/${it.notificationId}/edit`)}>
                                                    編集
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="row align-items-center justify-content-between py-2 pe-0 fs-9">
                            <div className="col-auto d-flex align-items-center gap-3">
                                <p className="mb-0 d-none d-sm-block me-1 fw-semibold text-body">
                                    {total === 0 ? '0 items' : `${first} to ${last} items of ${total}`}
                                </p>
                            </div>

                            <div className="col-auto d-flex align-items-center">
                                <button type="button" className="page-link" onClick={goPrev} disabled={page <= 1}>
                                    <span className="fas fa-chevron-left" />
                                </button>

                                <ul className="mb-0 pagination">
                                    {pages.map(p => (
                                        <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                                            <button type="button" className="page-link" onClick={() => setPage(p)}>
                                                {p}
                                            </button>
                                        </li>
                                    ))}
                                </ul>

                                <button type="button" className="page-link pe-0" onClick={goNext} disabled={page >= pageCount}>
                                    <span className="fas fa-chevron-right" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
